import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { IonButton, IonIcon, IonItem, IonLabel, IonList } from '@ionic/react';
import {
  checkmarkCircle,
  close,
  closeCircle,
  listOutline,
  search,
} from 'ionicons/icons';
import { ContactLogic, type Contact } from '@/modules/contact';
import { PhoneLogic } from '@/modules/phone';
import { FooterDock, FooterDockRow } from '@/modules/footer';
import {
  pxToRem,
  footerControlSurfaceStyle,
  footerControlRoundButtonStyle,
} from '@/modules/config/constants';
import styles from './SearchableContactList.module.css';

export interface SearchableContactListProps {
  contacts: readonly Contact[];
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  sparseHint?: React.ReactNode;
  onContactClick?: (contact: Contact) => void;
  disabledContacts?: readonly string[];
  /**
   * Per-contact notice badges, keyed by `localId`. Each badge is drawn *above*
   * the grey-out dimming applied to disabled rows so it stays legible. Use for
   * explaining why a disabled contact can't be picked — different rows can carry
   * different reasons ("No U.S. phone", "Already introduced").
   */
  disabledNotices?: Readonly<Record<string, string>>;
  /**
   * The contacts currently selected, by `localId`. Each gets a check mark at
   * the trailing edge of its row, and tapping it calls `onContactDeselect`
   * rather than `onContactClick`, so a picker can offer a selection that is
   * undone the same way it was made.
   */
  selectedContacts?: readonly string[];
  /** Called with a selected contact whose row was tapped. Pass it with `selectedContacts`. */
  onContactDeselect?: (contact: Contact) => void;
  searchPlaceholder?: string;
  testId?: string;
  footerElemWhenNotSearching?: React.ReactElement;
  /**
   * The page's primary action row (e.g. Export/Import, or Next). Rendered as the
   * bottom-pinned row of the footer dock — docked below the floating search pill
   * and automatically hidden while the search keyboard is up, so it frees that
   * space for results. The search pill rests directly atop its measured height.
   */
  actionRow?: React.ReactNode;
  listingCategoriesToSearch?: readonly string[];
  onListingCategoryClick?: (category: string) => void;
}

interface ContactGroup {
  letter: string;
  contacts: Contact[];
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function sortContacts(contacts: readonly Contact[]): Contact[] {
  return [...contacts].sort((a, b) => {
    const aLast = a.lastName.trim();
    const bLast = b.lastName.trim();
    const lastCompare = compareText(
      aLast || ContactLogic.displayName(a),
      bLast || ContactLogic.displayName(b)
    );
    if (lastCompare !== 0) return lastCompare;

    const firstCompare = compareText(a.firstName.trim(), b.firstName.trim());
    if (firstCompare !== 0) return firstCompare;

    return compareText(
      ContactLogic.displayName(a),
      ContactLogic.displayName(b)
    );
  });
}

function groupContacts(contacts: readonly Contact[]): ContactGroup[] {
  const groups: ContactGroup[] = [];
  for (const contact of contacts) {
    const initialSource =
      contact.lastName.trim() || ContactLogic.displayName(contact).trim();
    const firstChar = initialSource.charAt(0).toUpperCase();
    const letter = /^[A-Z]$/.test(firstChar) ? firstChar : '#';
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.letter === letter) {
      lastGroup.contacts.push(contact);
    } else {
      groups.push({ letter, contacts: [contact] });
    }
  }
  return groups;
}

function matchesContact(contact: Contact, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  if (
    ContactLogic.nameSearchText(contact).toLowerCase().includes(normalizedQuery)
  ) {
    return true;
  }

  if (!PhoneLogic.isPartialPhone(query)) {
    return false;
  }

  return contact.tels.some(tel =>
    PhoneLogic.matchesPhoneSearch(tel.value, query)
  );
}

function matchesListingCategory(category: string, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return false;
  return category.toLowerCase().includes(normalizedQuery);
}

// Height of the search row's pill. The actual value lives in CSS as
// --keyboard-pinned-row-height (used by the pill styling and the list-clearance calc);
// this CSS reference keeps the math reading from that single source. The
// keyboard-anchor calc itself lives in the footer module (useKeyboardPin).
const KEYBOARD_PINNED_ROW_HEIGHT_VAR = 'var(--keyboard-pinned-row-height)';

// Estimated resting position of the search row when the keyboard is closed: one
// --footer-dock-gap above the bottom action row (Export/Import or Next), which
// sits a default action-row height above its safe-area clearance. The dock itself
// positions the pill from the measured action-row height; this estimate is only
// used to size the list's bottom clearance below (the list can't read the dock's
// runtime measurement), so an approximation is fine.
const SEARCH_ROW_RESTING_BOTTOM =
  'calc(var(--footer-dock-bottom) + var(--footer-action-height) + var(--footer-dock-gap))';

// Bottom padding the scrolling list needs so its last rows clear the floating
// docks that overlay the screen bottom: the resting search pill (which itself
// sits SEARCH_ROW_RESTING_BOTTOM up) plus the pill height plus one more gap of
// breathing room. Derived from the dock geometry so it stays correct on every
// device (--footer-dock-bottom folds in the safe area); the keyboard inset is
// added on top when the keyboard is open (see listPaddingBottom).
const LIST_BOTTOM_CLEARANCE = `calc(${SEARCH_ROW_RESTING_BOTTOM} + var(--keyboard-pinned-row-height) + var(--footer-dock-gap))`;

const listShellStyle: React.CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#fff',
};

const sectionHeaderStyle: React.CSSProperties = {
  padding: '18px 20px 8px',
  color: '#8e8e93',
  fontSize: pxToRem(20),
  lineHeight: pxToRem(24),
  fontWeight: 400,
};

const itemStyle: React.CSSProperties = {
  '--padding-start': '20px',
  '--inner-padding-end': '20px',
  '--min-height': '62px',
  '--background': '#fff',
  '--border-color': 'transparent',
  '--border-style': 'none',
  '--border-width': '0px',
  '--inner-border-width': '0px',
} as React.CSSProperties;

const textEdgeSeparatorLeft = 76;
const iconEdgeSeparatorLeft = 20;

const contactRowShellStyle: React.CSSProperties = {
  backgroundColor: '#fff',
};

const separatorStyle: React.CSSProperties = {
  height: 1,
  backgroundColor: '#e5e5ea',
  pointerEvents: 'none',
};

const avatarStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  flex: '0 0 44px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
  color: '#fff',
  fontSize: pxToRem(17),
  fontWeight: 700,
  background: 'linear-gradient(135deg, #bad2f0 0%, #7986cb 100%)',
};

const nameStyle: React.CSSProperties = {
  margin: 0,
  color: '#111',
  fontSize: pxToRem(17),
  lineHeight: pxToRem(22),
  fontWeight: 400,
};

const phoneStyle: React.CSSProperties = {
  margin: '1px 0 0',
  color: '#8e8e93',
  fontSize: pxToRem(14),
  lineHeight: pxToRem(18),
};

// Layer holding the notice badge. Spans the row vertically and centers the badge
// so it lines up with the contact name, while right-justifying it. zIndex keeps it
// above the row's grey-out dimming.
const noticeBadgeLayerStyle: React.CSSProperties = {
  position: 'absolute',
  left: 76,
  right: 16,
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  zIndex: 2,
  pointerEvents: 'none',
};

const noticeBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  padding: '2px 8px',
  borderRadius: 10,
  backgroundColor: '#fff4e5',
  color: '#9a6700',
  fontSize: pxToRem(11),
  lineHeight: pxToRem(15),
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SearchableContactList: React.FC<SearchableContactListProps> = ({
  contacts,
  isLoading = false,
  emptyState,
  sparseHint,
  onContactClick,
  disabledContacts = [],
  disabledNotices,
  selectedContacts = [],
  onContactDeselect,
  searchPlaceholder = 'Search',
  testId = 'contact-list',
  footerElemWhenNotSearching,
  actionRow,
  listingCategoriesToSearch = [],
  onListingCategoryClick,
}) => {
  const searchInputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // The floating search row is rendered into the enclosing ion-page (a sibling
  // of ion-content) rather than inline — see the portal note near its render.
  const [searchRowTarget, setSearchRowTarget] = useState<HTMLElement | null>(
    null
  );

  const disabledContactSet = useMemo(
    () => new Set(disabledContacts),
    [disabledContacts]
  );
  const selectedContactSet = useMemo(
    () => new Set(selectedContacts),
    [selectedContacts]
  );
  const normalizedQuery = query.trim();
  const isSearching = isSearchFocused || normalizedQuery.length > 0;

  const sortedContacts = useMemo(() => sortContacts(contacts), [contacts]);
  const visibleContacts = useMemo(
    () =>
      normalizedQuery
        ? sortedContacts.filter(contact => matchesContact(contact, query))
        : sortedContacts,
    [normalizedQuery, query, sortedContacts]
  );
  const contactGroups = useMemo(
    () => groupContacts(visibleContacts),
    [visibleContacts]
  );
  const visibleListingCategories = useMemo(
    () =>
      normalizedQuery
        ? [...listingCategoriesToSearch]
            .filter(category => matchesListingCategory(category, query))
            .sort(compareText)
        : [],
    [listingCategoriesToSearch, normalizedQuery, query]
  );
  const listingCategoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const category of listingCategoriesToSearch) {
      counts.set(category, 0);
    }
    for (const contact of contacts) {
      for (const category of contact.listingCategories) {
        const matchingCategory = listingCategoriesToSearch.find(
          candidate =>
            candidate.trim().toLowerCase() === category.trim().toLowerCase()
        );
        if (matchingCategory) {
          counts.set(matchingCategory, (counts.get(matchingCategory) ?? 0) + 1);
        }
      }
    }
    return counts;
  }, [contacts, listingCategoriesToSearch]);
  const hasSearchResults =
    visibleContacts.length > 0 || visibleListingCategories.length > 0;

  // The search row holds a position:fixed input. On iOS, when that input is a
  // descendant of the scrollable IonContent and the keyboard opens, Safari
  // repeatedly scrolls IonContent to keep the focused input in view — fighting
  // the user's own scrolling, pinning the bottom of the list (with its empty
  // filler) on screen and snapping the upper results back under the header when
  // you try to reach them. Rendering the row into the enclosing page (a sibling
  // of ion-content, the same place the share wizard puts its Next button) takes
  // the input out of the scroller, so Safari has nothing to chase. It also keeps
  // the row from being clipped by ion-content, which (without `fullscreen`) no
  // longer spans to the bottom of the screen. Ionic React renders the page
  // wrapper as a <div class="ion-page"> — not an <ion-page> element — so we match
  // by class. Fall back to inline rendering when there's no page wrapper (e.g.
  // the jsdom test environment).
  useLayoutEffect(() => {
    setSearchRowTarget(
      (shellRef.current?.closest('.ion-page') as HTMLElement | null) ?? null
    );
  }, []);

  // Filtering collapses the list to far fewer rows, but the scroller keeps its
  // previous offset, which can strand the few remaining matches. Snap the
  // scroller back to the top on every query change so results start at top.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    // The list can scroll within a bounded ancestor (e.g. RecipientsStep caps
    // the contact region so its scrollbar stays clear of the recipient box's)
    // rather than within ion-content. Reset the nearest such ancestor; its
    // overflow lives in the light DOM, so we can find it by walking up.
    let ancestor = shell.parentElement;
    while (ancestor) {
      const { overflowY } = window.getComputedStyle(ancestor);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        ancestor.scrollTop = 0;
        break;
      }
      ancestor = ancestor.parentElement;
    }

    // ion-content scrolls via its shadow DOM (not reachable by the walk above),
    // so reset it through its own API when it's the scroller (the address book).
    const content = shell.closest('ion-content') as
      | (HTMLElement & { scrollToTop?: (duration?: number) => Promise<void> })
      | null;
    if (!content) return;
    if (typeof content.scrollToTop === 'function') {
      void content.scrollToTop(0);
      return;
    }
    const scroller =
      content.shadowRoot?.querySelector<HTMLElement>('.inner-scroll');
    if (scroller) scroller.scrollTop = 0;
  }, [normalizedQuery]);

  // Focusing the fixed search input makes some browsers nudge the (overflow:hidden)
  // page wrapper a couple of pixels to scroll the input "into view", which shifts
  // the whole list above it — most visibly on desktop, where no keyboard opens to
  // explain the movement. The wrapper is never meant to scroll, so pin it to the
  // top while the field is focused. (A no-op when nothing nudged it.)
  useEffect(() => {
    const target = searchRowTarget;
    if (!isSearchFocused || !target) return;
    const pin = () => {
      if (target.scrollTop !== 0) target.scrollTop = 0;
    };
    pin();
    target.addEventListener('scroll', pin, { passive: true });
    return () => target.removeEventListener('scroll', pin);
  }, [isSearchFocused, searchRowTarget]);

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const clearQueryAndFocus = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const closeSearch = () => {
    setQuery('');
    setIsSearchFocused(false);
    inputRef.current?.blur();
  };

  const handleContactClick = (contact: Contact) => {
    if (disabledContactSet.has(contact.localId)) return;
    if (selectedContactSet.has(contact.localId)) {
      onContactDeselect?.(contact);
    } else {
      onContactClick?.(contact);
    }
  };

  const renderContact = (contact: Contact, separatorLeft?: number) => {
    const isDisabled = disabledContactSet.has(contact.localId);
    const isSelected = selectedContactSet.has(contact.localId);
    const notice = disabledNotices?.[contact.localId];
    const phone = ContactLogic.getFormattedPhone(contact);
    return (
      <div
        key={contact.localId}
        // Marks the row as one contact's, so an enclosing scroll region can work
        // out which contacts are actually on screen (#581) without reaching for
        // test ids.
        data-contact-id={contact.localId}
        style={
          notice
            ? { ...contactRowShellStyle, position: 'relative' }
            : contactRowShellStyle
        }
      >
        <IonItem
          button={!isDisabled}
          detail={false}
          disabled={isDisabled}
          lines="none"
          onClick={() => handleContactClick(contact)}
          style={{
            ...itemStyle,
            opacity: isDisabled ? 0.42 : 1,
            cursor: isDisabled ? 'default' : 'pointer',
          }}
          aria-pressed={isSelected}
          data-testid={`contact-row-${contact.localId}`}
        >
          <div slot="start" style={avatarStyle} aria-hidden="true">
            {ContactLogic.getInitials(contact)}
          </div>
          <IonLabel>
            <h2 style={nameStyle}>{ContactLogic.displayName(contact)}</h2>
            {phone ? <p style={phoneStyle}>{phone}</p> : null}
          </IonLabel>
          {isSelected ? (
            <span
              slot="end"
              className={styles.selectedMark}
              data-testid={`contact-selected-${contact.localId}`}
            >
              <IonIcon icon={checkmarkCircle} aria-hidden="true" />
            </span>
          ) : null}
        </IonItem>
        {/* Notice badge is a sibling of (not nested in) the dimmed IonItem, so the
            row's grey-out opacity never applies to it — it stays legible above the
            grey-out layer. */}
        {notice ? (
          <div style={noticeBadgeLayerStyle}>
            <span
              style={noticeBadgeStyle}
              data-testid={`contact-badge-${contact.localId}`}
            >
              {notice}
            </span>
          </div>
        ) : null}
        {separatorLeft !== undefined ? (
          <div
            aria-hidden="true"
            data-testid={`contact-separator-${contact.localId}`}
            style={{ ...separatorStyle, marginLeft: separatorLeft }}
          />
        ) : null}
      </div>
    );
  };

  const renderListingCategory = (category: string) => {
    const contactCount = listingCategoryCounts.get(category) ?? 0;
    return (
      <div key={category} style={contactRowShellStyle}>
        <IonItem
          button={Boolean(onListingCategoryClick)}
          detail={false}
          lines="none"
          onClick={() => onListingCategoryClick?.(category)}
          style={{
            ...itemStyle,
            cursor: onListingCategoryClick ? 'pointer' : 'default',
          }}
          data-testid={`listing-category-row-${category}`}
        >
          <div
            slot="start"
            style={{
              ...avatarStyle,
              background: '#eef2ff',
              color: '#4f46e5',
            }}
            aria-hidden="true"
          >
            <IonIcon icon={listOutline} style={{ fontSize: pxToRem(24) }} />
          </div>
          <IonLabel>
            <h2 style={nameStyle}>{category}</h2>
            <p style={phoneStyle}>
              ({contactCount} contact{contactCount === 1 ? '' : 's'})
            </p>
          </IonLabel>
        </IonItem>
      </div>
    );
  };

  const listBody = (
    <>
      {isLoading ? (
        <IonList>
          {[1, 2, 3].map(index => (
            <IonItem
              key={`skeleton-${index}`}
              lines="none"
              style={{
                ...itemStyle,
                '--border-color': 'transparent',
                '--border-style': 'none',
                '--border-width': '0px',
                '--inner-border-width': '0px',
              }}
              data-testid="contact-skeleton-row"
            >
              <div
                slot="start"
                style={{
                  ...avatarStyle,
                  background: '#e0e0e0',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`,
                }}
              />
              <IonLabel>
                <div
                  style={{
                    height: 18,
                    width: '60%',
                    backgroundColor: '#e0e0e0',
                    borderRadius: 4,
                    marginBottom: 6,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1}s`,
                  }}
                />
                <div
                  style={{
                    height: 14,
                    width: '42%',
                    backgroundColor: '#e0e0e0',
                    borderRadius: 4,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1}s`,
                  }}
                />
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      ) : sparseHint &&
        contacts.length === 0 &&
        !normalizedQuery &&
        listingCategoriesToSearch.length === 0 ? (
        <div
          style={{ textAlign: 'center', padding: '40px 24px', color: '#666' }}
        >
          {sparseHint}
        </div>
      ) : contacts.length === 0 && listingCategoriesToSearch.length === 0 ? (
        emptyState
      ) : normalizedQuery && !hasSearchResults ? (
        <div
          style={{
            minHeight: 'calc(100vh - 260px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '80px 24px 160px',
          }}
          data-testid="search-empty-state"
        >
          <IonIcon
            icon={search}
            aria-hidden="true"
            style={{
              color: '#8e8e93',
              fontSize: pxToRem(74),
              marginBottom: 24,
            }}
          />
          <h2
            style={{
              margin: 0,
              color: '#000',
              fontSize: pxToRem(30),
              lineHeight: pxToRem(36),
              fontWeight: 700,
            }}
          >
            No Results for &quot;{normalizedQuery}&quot;
          </h2>
          <p
            style={{
              margin: '10px 0 0',
              color: '#8e8e93',
              fontSize: pxToRem(21),
              lineHeight: pxToRem(28),
            }}
          >
            Check the spelling or try a new search.
          </p>
        </div>
      ) : normalizedQuery ? (
        <IonList style={{ background: '#fff' }}>
          {visibleContacts.map((contact, index) =>
            renderContact(
              contact,
              index < visibleContacts.length - 1
                ? textEdgeSeparatorLeft
                : undefined
            )
          )}
          {visibleListingCategories.length > 0 ? (
            <>
              <div style={sectionHeaderStyle}>Matching contact lists</div>
              {visibleListingCategories.map(renderListingCategory)}
            </>
          ) : null}
        </IonList>
      ) : (
        <>
          <IonList style={{ background: '#fff' }}>
            {contactGroups.map((group, groupIndex) => (
              <React.Fragment key={group.letter}>
                <div style={sectionHeaderStyle}>{group.letter}</div>
                {group.contacts.map((contact, contactIndex) => {
                  const isLastInGroup =
                    contactIndex === group.contacts.length - 1;
                  const hasNextGroup = groupIndex < contactGroups.length - 1;
                  const separatorLeft = isLastInGroup
                    ? hasNextGroup
                      ? iconEdgeSeparatorLeft
                      : undefined
                    : textEdgeSeparatorLeft;

                  return renderContact(contact, separatorLeft);
                })}
              </React.Fragment>
            ))}
          </IonList>
          {sparseHint && contacts.length < 3 && !normalizedQuery && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 24px',
                color: '#666',
              }}
            >
              {sparseHint}
            </div>
          )}
        </>
      )}
    </>
  );

  // The footer dock stacks two rows: the page's action row (the default bottom
  // tier) and the floating search pill (pin="keyboard"). FooterDock handles the
  // keyboard reconciliation itself — resting the pill one gap above the action
  // row, rising above the keyboard, anchoring on iOS, and hiding the action row
  // while the keyboard is up — so the consumer just renders both rows (and the
  // pill opts out of the centered content box with `bare`).
  const dock = (
    // No scrim: every control in the dock (the action-row buttons, the search
    // pill, the close/add buttons) paints its own solid background, so the
    // scrolling list can show through the gaps between them without any text
    // jumbling with a button's label. Consumers keep their action-row buttons
    // solid (e.g. an outline button needs an explicit `--background`).
    <FooterDock background="transparent">
      {actionRow ? <FooterDockRow landmark>{actionRow}</FooterDockRow> : null}
      <FooterDockRow pin="keyboard" bare>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 20px',
          }}
        >
          <div
            style={{
              flex: 1,
              height: KEYBOARD_PINNED_ROW_HEIGHT_VAR,
              borderRadius: `calc(${KEYBOARD_PINNED_ROW_HEIGHT_VAR} / 2)`,
              ...footerControlSurfaceStyle,
              boxShadow: '0 14px 36px rgba(0, 0, 0, 0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 10px 0 14px',
              minWidth: 0,
            }}
          >
            <label
              htmlFor={searchInputId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 1,
                minWidth: 0,
                cursor: 'text',
              }}
            >
              <IonIcon
                icon={search}
                aria-hidden="true"
                style={{
                  color: '#8e8e93',
                  fontSize: pxToRem(22),
                  flex: '0 0 auto',
                }}
              />
              <input
                id={searchInputId}
                ref={inputRef}
                value={query}
                onChange={event => setQuery(event.target.value)}
                onFocus={handleSearchFocus}
                // Clearing focus when the field blurs (e.g. tapping a contact
                // closes the keyboard) restores the consumer's action row, which is
                // hidden only while the keyboard is up.
                onBlur={() => setIsSearchFocused(false)}
                placeholder={searchPlaceholder}
                inputMode="search"
                enterKeyHint="search"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                aria-label={searchPlaceholder}
                style={{
                  border: 0,
                  outline: 0,
                  background: 'transparent',
                  minWidth: 0,
                  flex: 1,
                  height: 36,
                  color: '#000',
                  fontSize: pxToRem(17),
                  lineHeight: pxToRem(22),
                  padding: 0,
                }}
              />
            </label>
            {query.length > 0 && (
              <button
                type="button"
                // Don't let pressing Clear blur the input — clearQueryAndFocus keeps
                // focus, so suppressing the blur avoids a focus-out/in flicker that
                // would briefly drop the pill and flash the action row.
                onMouseDown={event => event.preventDefault()}
                onPointerDown={event => event.preventDefault()}
                onClick={event => {
                  event.stopPropagation();
                  clearQueryAndFocus();
                }}
                aria-label="Clear search"
                style={{
                  border: 0,
                  background: 'transparent',
                  color: '#8e8e93',
                  padding: 0,
                  width: 30,
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <IonIcon icon={closeCircle} style={{ fontSize: pxToRem(22) }} />
              </button>
            )}
          </div>

          {isSearching ? (
            <IonButton
              fill="clear"
              onClick={closeSearch}
              aria-label="Close search"
              className="footer-glass-button footer-glass-round"
              style={
                {
                  ...footerControlRoundButtonStyle,
                  width: 44,
                  height: 44,
                  '--border-radius': '50%',
                  flex: '0 0 44px',
                } as React.CSSProperties
              }
            >
              <IonIcon
                slot="icon-only"
                icon={close}
                style={{ fontSize: pxToRem(28) }}
              />
            </IonButton>
          ) : (
            footerElemWhenNotSearching
          )}
        </div>
      </FooterDockRow>
    </FooterDock>
  );

  // On iOS the layout viewport doesn't shrink for the keyboard, so the list's
  // last rows sit behind it. Extend the bottom padding by the keyboard inset
  // (published as --footer-keyboard-inset by the keyboard-pinned row) so they can
  // scroll clear of the keyboard (and the search row above it).
  const listPaddingBottom = `calc(${LIST_BOTTOM_CLEARANCE} + var(--footer-keyboard-inset, 0px))`;

  return (
    <div ref={shellRef} style={listShellStyle} data-testid={testId}>
      <div style={{ paddingBottom: listPaddingBottom }}>{listBody}</div>
      {/* Render the footer dock outside ion-content (into the ion-page) so iOS
          doesn't scroll the list to chase the fixed search input, and so the
          action row sits at the page level — see the note on the portal-target
          effect above. */}
      {searchRowTarget ? createPortal(dock, searchRowTarget) : dock}
    </div>
  );
};

export default SearchableContactList;
